import { useEffect, useRef } from "react";

import { identifyCompany } from "@/shared/lib/analytics";

/**
 * SA-1166: associa o usuário à Company como PostHog Group (`posthog.group`)
 * assim que a company ativa passa a ser conhecida no client.
 *
 * Por que um hook dedicado (e não dentro do queryFn do `/user/me`): no fim do
 * onboarding o `bffUser` (já com `companyId`) é escrito direto no cache via
 * `queryClient.setQueryData` — que **não** dispara o queryFn. Se a chamada de
 * `identifyCompany` viver só no queryFn, ela nunca roda nesse caminho e os
 * eventos client subsequentes (checkout etc.) saem **sem** o group `company`,
 * quebrando os funis group-aggregated que cruzam client↔server. Reagindo à
 * mudança de `companyId` (venha de refetch OU de setQueryData) o group é setado
 * de forma confiável.
 *
 * Idempotente: só dispara quando o `companyId` muda de fato.
 *
 * Quando o `companyId` fica falsy (logout limpa o cache do usuário) o ref é
 * resetado: o `logout()` do AuthProvider chama `posthog.reset()`, que apaga a
 * associação do Group. Sem esse reset, um logout→login na MESMA company veria
 * `lastIdentified` ainda com o id antigo e pularia `identifyCompany` — deixando
 * os eventos client sem `$groups.company` após o reset.
 */
export function useIdentifyActiveCompany(companyId?: string | null): void {
  const lastIdentified = useRef<string | null>(null);

  useEffect(() => {
    if (!companyId) {
      lastIdentified.current = null;
      return;
    }
    if (lastIdentified.current === companyId) return;
    lastIdentified.current = companyId;
    identifyCompany(companyId);
  }, [companyId]);
}
